import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/hooks/useSubscription";
import { SeoHead } from "@/components/SeoHead";
import { track } from "@/lib/analyticsEvents";
import { TIER_PRICING, type Tier } from "@/lib/tier";

const PAID_TIERS: ReadonlySet<Tier> = new Set(["starter", "plus", "pro", "elite", "agency"]);

/**
 * Parse a price_id like "plus_monthly" or "pro_yearly" into a Plausible
 * revenue payload. Returns null for unrecognized priceIds (e.g. legacy
 * Stripe-internal IDs from before we standardized on lookup keys).
 */
const revenuePayloadFor = (priceId: string | null | undefined) => {
  if (!priceId) return null;
  const [tierPart, billingPart] = priceId.split("_");
  if (!tierPart || !billingPart) return null;
  if (!PAID_TIERS.has(tierPart as Tier)) return null;
  if (billingPart !== "monthly" && billingPart !== "yearly") return null;
  const tier = tierPart as keyof typeof TIER_PRICING;
  const billing = billingPart as "monthly" | "yearly";
  const amount = TIER_PRICING[tier]?.[billing];
  if (!amount) return null;
  return { amount, currency: "USD", priceId, tier, billing };
};

const CheckoutReturn = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { isActive, subscription, refresh } = useSubscription();
  const [waited, setWaited] = useState(0);
  const firedRef = useRef<string | null>(null);

  useEffect(() => {
    document.title = "Payment complete — Excerptus";
  }, []);

  // Poll for the subscription row for up to ~12s while the webhook catches up.
  useEffect(() => {
    if (isActive) return;
    if (waited > 12) return;
    const t = setTimeout(() => {
      refresh();
      setWaited((w) => w + 1.5);
    }, 1500);
    return () => clearTimeout(t);
  }, [isActive, waited, refresh]);

  // Fire `purchase` once per session_id as soon as the subscription row lands.
  useEffect(() => {
    if (!isActive) return;
    if (!sessionId) return;
    if (firedRef.current === sessionId) return;
    const payload = revenuePayloadFor(subscription?.price_id);
    if (!payload) return;
    firedRef.current = sessionId;
    track("purchase", payload);
    track("checkout_succeeded", { priceId: payload.priceId });
  }, [isActive, sessionId, subscription?.price_id]);

  const ready = isActive;
  const timedOut = !ready && waited > 12;

  return (
    <>
    <SeoHead title="Checkout complete — Excerptus" description="Thanks for subscribing to Excerptus." noindex />
    <div className="min-h-dvh flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6 rounded-xl border border-border bg-card p-8 shadow-soft">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          {ready ? (
            <CheckCircle2 className="h-8 w-8 text-primary" />
          ) : (
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          )}
        </div>
        <div className="space-y-2">
          <h1 className="font-serif text-2xl font-semibold">
            {ready ? "You're all set" : "Activating your subscription…"}
          </h1>
          <p className="text-muted-foreground text-sm">
            {ready
              ? "Your plan is active. Enjoy the new features."
              : timedOut
                ? "This is taking longer than usual. Your plan should activate within a few minutes — refresh Settings if you don't see it."
                : "Hang tight while we confirm your payment with our billing system."}
          </p>
        </div>
        {sessionId && (
          <p className="text-xs text-muted-foreground/70 break-all">Session: {sessionId}</p>
        )}
        <div className="flex flex-col gap-2">
          <Link to="/dashboard">
            <Button className="w-full" disabled={!ready && !timedOut}>
              Go to my dashboard
            </Button>
          </Link>
          <Link to="/settings">
            <Button variant="outline" className="w-full">View settings</Button>
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default CheckoutReturn;
